import {
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  $convertFromMarkdownString,
  $convertToMarkdownString,
} from "@lexical/markdown";
import {
  ArrowBack,
  ExpandMore,
  FiberManualRecord,
  OpenInNew,
  VisibilityOutlined,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  PartnerOrgSummary,
  ResponseNote,
  ResponseNotes,
  RmsIncidentCodes,
  SurveyConfiguration,
  getOrgSurveyConfigPortal,
  getOrgSurveyVersionOverview,
  listPartnerOrg,
  postDeactivateSurveyConfig,
  postUpdateManagedOnlineReportingSurveyTemplates,
  updateSurvey,
} from "@rubi/api-generated";
import { EditorState, LexicalEditor as LexicalEditorType } from "lexical";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import LexicalEditor, { customTransformers } from "@/common/LexicalEditor";
import { getServiceConfig } from "@/common/serviceConfigurations";
import {
  getServicesBaseHost,
  resolveApiRegion,
} from "@/components/online-reporting/citizen-engage/utils";
import { ILocaleObject } from "@/components/online-reporting/types";
import { AuthStateContext } from "@/contexts/AuthContext";
import { useDevEnvironment } from "@/contexts/DevContext";

import { useFormBuilderContext } from "./FormContextProvider";
import { extractLocaleStringFromField, formatVersionNumber } from "./utils";

import "./SurveyConfigurationEdit.scss";

const REPORT_ICONS = [
  "/report-icons/bail_breach.png",
  "/report-icons/bicycle_registration.png",
  "/report-icons/bicycle_registration_v2.png",
  "/report-icons/bicycle_theft.png",
  "/report-icons/break_and_enter.png",
  "/report-icons/bullying.png",
  "/report-icons/camera_registry.png",
  "/report-icons/choose.png",
  "/report-icons/choose_transparent.png",
  "/report-icons/ciroc.png",
  "/report-icons/correctional_facility.png",
  "/report-icons/damage_property.png",
  "/report-icons/damage_vehicle.png",
  "/report-icons/dog_incident.png",
  "/report-icons/drug_search_request.png",
  "/report-icons/drug_seizure_report.png",
  "/report-icons/elder_abuse.png",
  "/report-icons/found_property.png",
  "/report-icons/fraud.png",
  "/report-icons/gas_station_driveoff.png",
  "/report-icons/graffiti.png",
  "/report-icons/harassing_communication.png",
  "/report-icons/hate.png",
  "/report-icons/keyholder_registration.png",
  "/report-icons/long_term_care_incident.png",
  "/report-icons/lost_item.png",
  "/report-icons/lost_license_plate.png",
  "/report-icons/missing_id.png",
  "/report-icons/odin_search.png",
  "/report-icons/road_watch.png",
  "/report-icons/school_bus_violation.png",
  "/report-icons/school_incident.png",
  "/report-icons/sexual_assault.png",
  "/report-icons/shoplifting.png",
  "/report-icons/supplementary.png",
  "/report-icons/suspicious_drug_activity.png",
  "/report-icons/theft.png",
  "/report-icons/theft_from_vehicle.png",
  "/report-icons/traffic_violaiton.png",
  "/report-icons/trespassing.png",
  "/report-icons/use_of_force.png",
  "/report-icons/vuln_persons_registry.png",
];

const emptyConfig: SurveyConfiguration = {
  id: "",
  name: "",
  survey_id: "",
  survey_class: "",
  description: {
    definition: "",
    image_url: "",
  },
  hidden: false,
  is_supplemental: false,
  is_registry: false,
  is_request: false,
  dedicated_supplemental_id: null,
  partner_org_id: null,
  external_url: "",
  auto_reject_modification_requested: false,
  sort_order: null,
  include_pdf_in_emails: true,
  include_images_in_pdf: false,
  include_attachments_in_rms_export: true,
  include_attachments_in_dems_export: true,
  export_to_rms: true,
  auto_approve_on_submit: false,
  auto_approve_linked_supplementals: false,
  sample_synopsis: "",
  export_as_message_template: true,
  rms_incident_codes: null,
  fields_to_exclude: [],
  cc_emails: [],
  supplemental_carry_over_keys: [],
};

const RMS_INCIDENT_CODE_FIELDS: {
  key: keyof RmsIncidentCodes;
  label: string;
  fallback?: string;
}[] = [
  {
    key: "incident_type",
    label: "Incident type",
    fallback: "Configuration name",
  },
  {
    key: "incident_ref_name",
    label: "Incident reference name",
    fallback: "Survey class",
  },
  { key: "ln_report_type", label: "LN report type" },
  { key: "ln_report_type_code", label: "LN report type code" },
  { key: "pri_code_x1", label: "Primary code X1" },
  { key: "pri_code_x2", label: "Primary code X2" },
  { key: "pri_code_x3", label: "Primary code X3" },
  { key: "pri_code_x4", label: "Primary code X4" },
  { key: "xref_code", label: "Cross-reference code" },
];

// Names the RMS export actually acts on. Anything else is only checked by the
// form linter, so it is offered as a suggestion list rather than a fixed set.
const EXCLUDABLE_EXPORT_FIELDS = [
  "items-stolen",
  "items-involved",
  "items-damaged",
  "items-lost",
  "ip_address",
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const jsonToText = (value: unknown): string =>
  value == null ? "" : JSON.stringify(value, null, 2);

const isPlainObject = (value: unknown): boolean =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const parseJsonField = (
  raw: string,
): { value: unknown; error: string | null } => {
  if (raw.trim() === "") return { value: null, error: null };
  try {
    return { value: JSON.parse(raw), error: null };
  } catch (e) {
    return {
      value: undefined,
      error: e instanceof Error ? e.message : "Invalid JSON",
    };
  }
};

const parseCcEmailsInput = (raw: string): string[] =>
  raw
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

const invalidCcEmails = (emails: string[]): string[] =>
  emails.filter((e) => !EMAIL_PATTERN.test(e));

const parseCarryOverKeysInput = (raw: string): string[] => [
  ...new Set(
    raw
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0),
  ),
];

type TabKey =
  | "general"
  | "submitter_response"
  | "review"
  | "export"
  | "translations";

const TAB_LABEL: Record<TabKey, string> = {
  general: "General",
  submitter_response: "Submitter response",
  review: "Review workflow",
  export: "Export & integrations",
  translations: "Translations",
};

const TAB_ORDER: TabKey[] = [
  "general",
  "submitter_response",
  "review",
  "export",
  "translations",
];

// Maps each editable field on SurveyConfiguration to the tab that owns it.
// Fields not listed here are either read-only identity (id) or fall back to "general".
const FIELD_TO_TAB: Partial<Record<keyof SurveyConfiguration, TabKey>> = {
  name: "general",
  survey_id: "general",
  survey_class: "general",
  description: "general",
  external_url: "general",
  sort_order: "general",
  is_supplemental: "general",
  is_registry: "general",
  is_request: "general",
  hidden: "general",

  confirmation_text: "submitter_response",
  dedicated_supplemental_id: "submitter_response",
  partner_org_id: "submitter_response",
  prefill_report_number_in_supplemental_url: "submitter_response",
  response_notes: "submitter_response",
  cc_emails: "submitter_response",
  supplemental_carry_over_keys: "submitter_response",

  sample_synopsis: "review",
  auto_reject_modification_requested: "review",
  auto_approve_on_submit: "review",
  auto_approve_linked_supplementals: "review",
  mandatory_crime_codes: "review",
  clear_assigned_to_on_supplemental_linked: "review",
  clear_assigned_to_on_resubmitted: "review",
  default_reviewer_details: "review",
  reviewer_details_survey: "review",

  export_to_rms: "export",
  rms_incident_codes: "export",
  fields_to_exclude: "export",
  include_attachments_in_rms_export: "export",
  include_attachments_in_dems_export: "export",
  include_pdf_in_emails: "export",
  include_images_in_pdf: "export",
  export_as_message_template: "export",

  translated_name_description: "translations",
};

const shallowEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (a == null || b == null) return a === b;
  if (typeof a !== "object" || typeof b !== "object") return false;
  return JSON.stringify(a) === JSON.stringify(b);
};

export const SurveyConfigurationEdit = () => {
  const { orgId, surveyClass } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const app = location.pathname.includes("rubiintel")
    ? "rubiintel"
    : "rubireportv2";
  const formsListPath = `/${app}/${orgId}/forms/configs`;
  const isNewConfig = window.location.pathname.includes("/new");
  const [activeTab, setActiveTab] = useState<TabKey>("general");

  const {
    surveyConfigs,
    setSurveyConfigs,
    surveysWithActiveVersions,
    setSurveysWithActiveVersions,
  } = useFormBuilderContext();
  const [config, setConfig] = useState<SurveyConfiguration>(emptyConfig);
  const [partnerOrgs, setPartnerOrgs] = useState<PartnerOrgSummary[]>([]);
  const [partnerOrgsError, setPartnerOrgsError] = useState<string | null>(null);
  const [originalConfig, setOriginalConfig] =
    useState<SurveyConfiguration | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);

  const [defaultReviewerJson, setDefaultReviewerJson] = useState("");
  const [defaultReviewerJsonError, setDefaultReviewerJsonError] = useState<
    string | null
  >(null);

  const [reviewerSurveyJson, setReviewerSurveyJson] = useState("");
  const [reviewerSurveyJsonError, setReviewerSurveyJsonError] = useState<
    string | null
  >(null);

  // The raw textarea content for the CC email list. We mirror this from
  // config.cc_emails so the user can type freely (with commas, whitespace,
  // partial entries) without us reformatting their input on every keystroke.
  const [ccEmailsInput, setCcEmailsInput] = useState("");
  const ccEmailsParsed = useMemo(
    () => parseCcEmailsInput(ccEmailsInput),
    [ccEmailsInput],
  );

  const [carryOverKeysInput, setCarryOverKeysInput] = useState("");
  const carryOverKeysParsed = useMemo(
    () => parseCarryOverKeysInput(carryOverKeysInput),
    [carryOverKeysInput],
  );

  useEffect(() => {
    listPartnerOrg()
      .then((res) => {
        setPartnerOrgs(res.data?.partner_orgs ?? []);
        setPartnerOrgsError(null);
      })
      .catch(() => {
        setPartnerOrgs([]);
        setPartnerOrgsError("Could not load partner organizations.");
      });
  }, []);
  const ccEmailsErrors = useMemo(
    () => invalidCcEmails(ccEmailsParsed),
    [ccEmailsParsed],
  );

  const { isDevEnv } = useDevEnvironment();
  const { currentUser } = useContext(AuthStateContext);
  const organizationConfig = getServiceConfig(
    orgId ?? currentUser?.rubi_org_id ?? "NA",
    isDevEnv,
  );
  const apiRegion = resolveApiRegion({
    sessionApiRegion: sessionStorage.getItem("apiRegion"),
    organizationApiRegion: organizationConfig.apiRegion,
    userApiRegion: currentUser?.api_region,
  });
  // The saved form on the public services site; shown as the Access URL and
  // used for the Preview Mode button. Null until the form has been saved.
  const publicFormUrl =
    originalConfig && !isNewConfig
      ? `https://${getServicesBaseHost(apiRegion, isDevEnv)}/${organizationConfig.servicesURLSlug}/online-reports/report/${originalConfig.survey_class}`
      : null;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const editorRef = useRef<any>(null);
  const editor: LexicalEditorType = editorRef.current;

  useEffect(() => {
    if (editor && config.description.definition) {
      const currentState: EditorState = editor.getEditorState();
      const markdownString = currentState.read(() =>
        $convertToMarkdownString(customTransformers, undefined, true),
      );
      if (config.description.definition !== markdownString) {
        editor.update(() => {
          $convertFromMarkdownString(
            config.description.definition,
            customTransformers,
            undefined,
            true,
          );
        });
      }
    }
  }, [editor, config.description.definition]);

  useEffect(() => {
    if (!editor) return;
    return editor.registerUpdateListener(() => {
      const currentState = editor.getEditorState();
      const markdownString = currentState.read(() =>
        $convertToMarkdownString(customTransformers, undefined, true),
      );

      setConfig((prev) => ({
        ...prev,
        description: { ...prev.description, definition: markdownString },
      }));
      setIsDirty(true);
    });
  }, [editor]);

  useEffect(() => {
    const fetchConfig = async () => {
      if (isNewConfig) {
        setConfig(emptyConfig);
        setOriginalConfig(emptyConfig);
        return;
      }

      try {
        // First try from context (already loaded via authenticated endpoint)
        const fromContext = surveyConfigs.find(
          (s) => s.survey_class === surveyClass,
        );
        if (fromContext) {
          setConfig({ ...emptyConfig, ...fromContext });
          setOriginalConfig({ ...emptyConfig, ...fromContext });
          setDefaultReviewerJson(
            jsonToText(fromContext.default_reviewer_details),
          );
          setReviewerSurveyJson(
            jsonToText(fromContext.reviewer_details_survey),
          );
          setCcEmailsInput((fromContext.cc_emails ?? []).join(", "));
          setCarryOverKeysInput(
            (fromContext.supplemental_carry_over_keys ?? []).join(", "),
          );
          return;
        }

        // Fallback: fetch from authenticated portal endpoint
        const response = await getOrgSurveyConfigPortal({
          path: { org_id: orgId! },
        });
        const match = response.data?.surveys?.find(
          (s) => s.survey_class === surveyClass,
        );
        if (match) {
          setConfig({ ...emptyConfig, ...match });
          setOriginalConfig({ ...emptyConfig, ...match });
          setDefaultReviewerJson(jsonToText(match.default_reviewer_details));
          setReviewerSurveyJson(jsonToText(match.reviewer_details_survey));
          setCcEmailsInput((match.cc_emails ?? []).join(", "));
          setCarryOverKeysInput(
            (match.supplemental_carry_over_keys ?? []).join(", "),
          );
        }
      } catch (error) {
        console.error(error);
      }
    };
    fetchConfig();
  }, [orgId, surveyClass]);

  useEffect(() => {
    const fetchSurveysWithVersions = async () => {
      if (surveysWithActiveVersions.length > 0 || !orgId) return;

      try {
        const surveysResponse = await getOrgSurveyVersionOverview({
          path: { org_id: orgId },
          query: { status: "active" },
        });
        setSurveysWithActiveVersions(surveysResponse?.data!.surveys || []);
      } catch (error) {
        console.error("Error fetching surveys:", error);
      }
    };
    fetchSurveysWithVersions();
  }, [orgId, surveysWithActiveVersions.length, setSurveysWithActiveVersions]);

  const handleChange =
    (field: keyof SurveyConfiguration) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value =
        event.target.type === "checkbox"
          ? (event.target as HTMLInputElement).checked
          : event.target.value;
      setConfig((prev) => (prev ? { ...prev, [field]: value } : emptyConfig));
      setIsDirty(true);
    };
  const handleNumberChange =
    (field: keyof SurveyConfiguration) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value;
      const value = raw === "" ? null : parseInt(raw, 10);
      if (value !== null && Number.isNaN(value)) return;
      setConfig((prev) => (prev ? { ...prev, [field]: value } : emptyConfig));
      setIsDirty(true);
    };

  const handleCcEmailsChange = (raw: string) => {
    setCcEmailsInput(raw);
    const parsed = parseCcEmailsInput(raw);
    setConfig((prev) => ({ ...prev, cc_emails: parsed }));
    setIsDirty(true);
  };

  const handleRmsCodeChange = (key: keyof RmsIncidentCodes, raw: string) => {
    setConfig((prev) => ({
      ...prev,
      rms_incident_codes: {
        ...(prev.rms_incident_codes ?? {}),
        [key]: raw === "" ? null : raw,
      },
    }));
    setIsDirty(true);
  };

  const handleCarryOverKeysChange = (raw: string) => {
    setCarryOverKeysInput(raw);
    setConfig((prev) => ({
      ...prev,
      supplemental_carry_over_keys: parseCarryOverKeysInput(raw),
    }));
    setIsDirty(true);
  };

  const handleExcludedFieldsChange = (next: string[]) => {
    const cleaned = Array.from(
      new Set(next.map((f) => f.trim()).filter((f) => f.length > 0)),
    );
    setConfig((prev) => ({ ...prev, fields_to_exclude: cleaned }));
    setIsDirty(true);
  };

  const handleJsonObjectChange =
    (
      field: "default_reviewer_details" | "reviewer_details_survey",
      setText: (raw: string) => void,
      setError: (message: string | null) => void,
    ) =>
    (raw: string) => {
      setText(raw);
      setIsDirty(true);
      const { value, error } = parseJsonField(raw);
      if (!error && value !== null && !isPlainObject(value)) {
        setError("Must be a JSON object");
        return;
      }
      setError(error);
      if (error) return;
      setConfig((prev) => ({ ...prev, [field]: value }));
    };

  const handleDefaultReviewerChange = handleJsonObjectChange(
    "default_reviewer_details",
    setDefaultReviewerJson,
    setDefaultReviewerJsonError,
  );

  const handleReviewerSurveyChange = handleJsonObjectChange(
    "reviewer_details_survey",
    setReviewerSurveyJson,
    setReviewerSurveyJsonError,
  );

  // Per-tab dirtiness — compared field-by-field against originalConfig,
  // so each tab's label shows a dot only when that tab owns a changed field.
  const dirtyTabs = useMemo(() => {
    const dirty = new Set<TabKey>();
    if (!originalConfig) return dirty;
    (Object.keys(FIELD_TO_TAB) as (keyof SurveyConfiguration)[]).forEach(
      (field) => {
        const tab = FIELD_TO_TAB[field];
        if (!tab) return;
        if (!shallowEqual(config[field], originalConfig[field])) {
          dirty.add(tab);
        }
      },
    );
    return dirty;
  }, [config, originalConfig]);

  const handleSave = async () => {
    if (isDuplicateSurveyClass(config.survey_class)) return;
    if (!isValidUrl(config.external_url || "")) return;
    if (defaultReviewerJsonError) return;
    if (reviewerSurveyJsonError) return;
    if (ccEmailsErrors.length > 0) return;
    if (isSaving) return;

    try {
      setIsSaving(true);
      const body = {
        survey_class: config.survey_class,
        survey_id: config.survey_id,
        name: config.name,
        description: config.description,
        is_supplemental: config.is_supplemental,
        is_registry: config.is_registry,
        is_request: config.is_request,
        hidden: config.hidden,
        external_url: config.external_url || null,
        auto_reject_modification_requested:
          config.auto_reject_modification_requested,
        sort_order: config.sort_order ?? undefined,
        include_pdf_in_emails: config.include_pdf_in_emails ?? undefined,
        include_images_in_pdf: config.include_images_in_pdf ?? undefined,
        include_attachments_in_rms_export:
          config.include_attachments_in_rms_export ?? undefined,
        include_attachments_in_dems_export:
          config.include_attachments_in_dems_export ?? undefined,
        export_to_rms: config.export_to_rms ?? undefined,
        rms_incident_codes: config.rms_incident_codes ?? undefined,
        fields_to_exclude: config.fields_to_exclude ?? [],
        auto_approve_on_submit: config.auto_approve_on_submit ?? undefined,
        auto_approve_linked_supplementals:
          config.auto_approve_linked_supplementals ?? undefined,
        sample_synopsis: config.sample_synopsis || undefined,
        export_as_message_template:
          config.export_as_message_template ?? undefined,
        mandatory_crime_codes: config.mandatory_crime_codes ?? undefined,
        default_reviewer_details: config.default_reviewer_details ?? null,
        reviewer_details_survey: config.reviewer_details_survey ?? null,
        clear_assigned_to_on_supplemental_linked:
          config.clear_assigned_to_on_supplemental_linked ?? undefined,
        clear_assigned_to_on_resubmitted:
          config.clear_assigned_to_on_resubmitted ?? undefined,
        prefill_report_number_in_supplemental_url: config.is_supplemental
          ? false
          : (config.prefill_report_number_in_supplemental_url ?? undefined),
        confirmation_text: config.confirmation_text ?? undefined,
        response_notes: config.response_notes ?? undefined,
        dedicated_supplemental_id: config.is_supplemental
          ? null
          : (config.dedicated_supplemental_id ?? null),
        partner_org_id: config.partner_org_id ?? null,
        cc_emails: ccEmailsParsed,
        supplemental_carry_over_keys: config.is_supplemental
          ? []
          : carryOverKeysParsed,
      };
      await updateSurvey({
        path: { org_id: orgId! },
        body,
      });
      await postUpdateManagedOnlineReportingSurveyTemplates({
        query: { rubi_org_id: orgId! },
      });
      const configsResponse = await getOrgSurveyConfigPortal({
        path: { org_id: orgId! },
      });
      const newConfigs = configsResponse?.data?.surveys ?? [];
      setSurveyConfigs(newConfigs);
      const saved = newConfigs.find(
        (s) => s.survey_class === config.survey_class,
      );
      const synced = saved ? { ...emptyConfig, ...saved } : config;
      setConfig(synced);
      setOriginalConfig(synced);
      setCcEmailsInput((synced.cc_emails ?? []).join(", "));
      setCarryOverKeysInput(
        (synced.supplemental_carry_over_keys ?? []).join(", "),
      );
      setIsDirty(false);
      if (isNewConfig && saved) {
        navigate(`/${app}/${orgId}/forms/configs/${saved.survey_class}`, {
          replace: true,
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const isDuplicateSurveyClass = (surveyClass: string) => {
    return surveyConfigs.some(
      (config) =>
        config.survey_class === surveyClass && config.id !== originalConfig?.id,
    );
  };

  const isValidUrl = (url: string): boolean => {
    if (!url || url.trim() === "") {
      return true; // Empty is valid (optional field)
    }
    try {
      const urlObj = new URL(url);
      return urlObj.protocol === "http:" || urlObj.protocol === "https:";
    } catch {
      return false;
    }
  };

  const getAvailableSurveyIds = () => {
    return surveysWithActiveVersions.map((item) => {
      const title = item.active_version?.configuration?.title;
      const name = title
        ? extractLocaleStringFromField(title as string | ILocaleObject)
        : `Survey ${formatVersionNumber(item.active_version)}`;
      return {
        id: item.parent_survey_id,
        name,
      };
    });
  };

  const handleBack = () => {
    navigate(formsListPath);
  };

  const handleDeactivateClick = () => setDeactivateModalOpen(true);
  const handleDeactivateConfirm = async () => {
    if (!originalConfig?.id || !orgId) return;
    try {
      setIsDeactivating(true);
      const response = await postDeactivateSurveyConfig({
        path: { org_id: orgId, config_id: originalConfig.id },
      });
      setSurveyConfigs(response.data?.surveys ?? []);
      // The deactivated survey no longer exists as a CONFIG, so re-sync to
      // delete its managed message template.
      await postUpdateManagedOnlineReportingSurveyTemplates({
        query: { rubi_org_id: orgId },
      });
      setDeactivateModalOpen(false);
      navigate(formsListPath);
    } catch (error) {
      console.error(error);
    } finally {
      setIsDeactivating(false);
    }
  };
  const handleDeactivateCancel = () => setDeactivateModalOpen(false);

  const renderTabLabel = (key: TabKey) => (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.75}
      component="span"
      sx={{ textTransform: "none" }}
    >
      <span>{TAB_LABEL[key]}</span>
      {dirtyTabs.has(key) && (
        <FiberManualRecord
          sx={{ fontSize: 10, color: "warning.main" }}
          aria-label="unsaved changes"
        />
      )}
    </Stack>
  );

  return (
    <Paper className="survey-config-edit">
      <Box sx={{ mb: 2 }}>
        <Button onClick={handleBack} startIcon={<ArrowBack />}>
          Back to Form Management
        </Button>
      </Box>

      <Typography variant="h4" gutterBottom>
        {isNewConfig
          ? "Create New Survey Configuration"
          : "Edit Survey Configuration"}
      </Typography>

      {publicFormUrl && (
        <div style={{ marginBottom: "1.5rem" }}>
          <Typography variant="subtitle1" gutterBottom>
            Access URL:
          </Typography>
          <Stack
            direction={isMobile ? "column" : "row"}
            spacing={2}
            sx={{ alignItems: isMobile ? "flex-start" : "center" }}
          >
            <a
              href={publicFormUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ wordBreak: "break-all" }}
            >
              {publicFormUrl}
            </a>
            <Button
              variant="outlined"
              size="small"
              startIcon={<VisibilityOutlined />}
              endIcon={<OpenInNew />}
              href={`${publicFormUrl}?reportReviewMode=true`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open in Preview Mode (opens in a new tab)"
              sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
            >
              Open in Preview Mode
            </Button>
          </Stack>
        </div>
      )}

      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v as TabKey)}
        sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}
      >
        {TAB_ORDER.map((key) => (
          <Tab key={key} value={key} label={renderTabLabel(key)} />
        ))}
      </Tabs>

      {activeTab === "general" && (
        <TabSection>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Name"
                value={config.name}
                onChange={handleChange("name")}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                select
                fullWidth
                label="Survey ID"
                value={config.survey_id}
                onChange={(e) => {
                  setConfig((prev) => ({
                    ...prev,
                    survey_id: e.target.value,
                  }));
                  setIsDirty(true);
                }}
              >
                {getAvailableSurveyIds().map((survey) => (
                  <MenuItem key={survey.id} value={survey.id}>
                    {survey.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Survey Class"
                value={config.survey_class}
                onChange={handleChange("survey_class")}
                helperText={
                  isDuplicateSurveyClass(config.survey_class)
                    ? "This survey class already exists. Please choose a unique value."
                    : "Unique identifier — appears in the public report URL"
                }
                error={isDuplicateSurveyClass(config.survey_class)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Sort order"
                value={config.sort_order ?? ""}
                onChange={handleNumberChange("sort_order")}
                helperText="Lower numbers appear first in lists"
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="External URL"
                value={config.external_url || ""}
                onChange={(e) => {
                  setConfig((prev) => ({
                    ...prev,
                    external_url: e.target.value,
                  }));
                  setIsDirty(true);
                }}
                helperText={
                  !isValidUrl(config.external_url || "")
                    ? "Please enter a valid URL starting with http:// or https://"
                    : "If set, the card opens this URL instead of the internal form"
                }
                error={!isValidUrl(config.external_url || "")}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack spacing={2}>
                <TextField
                  select
                  fullWidth
                  label="Configuration Icon"
                  value={config.description.image_url}
                  onChange={(e) => {
                    setConfig((prev) => ({
                      ...prev,
                      description: {
                        ...prev.description,
                        image_url: e.target.value,
                      },
                    }));
                    setIsDirty(true);
                  }}
                >
                  {REPORT_ICONS.map((iconPath) => (
                    <MenuItem key={iconPath} value={iconPath}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "1rem",
                        }}
                      >
                        <img
                          src={iconPath}
                          alt="icon preview"
                          style={{ width: "24px", height: "24px" }}
                        />
                        {iconPath.split("/").pop()}
                      </div>
                    </MenuItem>
                  ))}
                </TextField>
                {config.description.image_url && (
                  <img
                    src={config.description.image_url}
                    alt="Configuration Icon"
                    style={{ maxWidth: "160px", maxHeight: "160px" }}
                  />
                )}
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack spacing={1}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.is_supplemental}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        setConfig((prev) => ({
                          ...prev,
                          is_supplemental: checked,
                          ...(checked
                            ? {
                                dedicated_supplemental_id: null,
                                prefill_report_number_in_supplemental_url: false,
                              }
                            : {}),
                        }));
                        setIsDirty(true);
                      }}
                    />
                  }
                  label={
                    <LabelWithHelp
                      label="Supplemental"
                      help="Mark this report type as a supplemental that links to other records"
                    />
                  }
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.is_registry}
                      onChange={handleChange("is_registry")}
                    />
                  }
                  label={
                    <LabelWithHelp
                      label="Registry"
                      help="Listed under registries instead of reports. Registry submissions skip reporting-party notification emails and are left out of report-type suggestions."
                    />
                  }
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.is_request}
                      onChange={handleChange("is_request")}
                    />
                  }
                  label={
                    <LabelWithHelp
                      label="Officer request form"
                      help="Completed only when an officer sends a request to a citizen. Request forms are excluded from managed message templates."
                    />
                  }
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.hidden}
                      onChange={handleChange("hidden")}
                    />
                  }
                  label={
                    <LabelWithHelp
                      label="Hidden"
                      help="Hide from the public list. The report is still accessible via direct link — useful for partner-only forms."
                    />
                  }
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1">Description</Typography>
              <LexicalEditor editorRef={editorRef} />
            </Grid>
          </Grid>
        </TabSection>
      )}

      {activeTab === "submitter_response" && (
        <TabSection>
          <Stack spacing={4}>
            <Box>
              <Typography variant="overline" color="text.secondary">
                Post-submit page
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Post-submit page text"
                value={config.confirmation_text ?? ""}
                onChange={handleChange("confirmation_text")}
                helperText="Message shown on the web page after the report is submitted (not the email)"
                sx={{ mt: 1 }}
              />
            </Box>

            <Box>
              <Typography variant="overline" color="text.secondary">
                Supplementary link in confirmation email
              </Typography>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <Tooltip
                  title={
                    config.is_supplemental
                      ? "Supplementary reports cannot have their own dedicated supplementary"
                      : ""
                  }
                  placement="top"
                  arrow
                  disableHoverListener={!config.is_supplemental}
                >
                  <span style={{ display: "block" }}>
                    <TextField
                      select
                      fullWidth
                      label="Dedicated supplementary report"
                      value={config.dedicated_supplemental_id ?? ""}
                      disabled={config.is_supplemental}
                      onChange={(e) => {
                        const v = e.target.value;
                        setConfig((prev) => ({
                          ...prev,
                          dedicated_supplemental_id: v === "" ? null : v,
                        }));
                        setIsDirty(true);
                      }}
                      helperText="The supplementary report URL placed in the confirmation email. Falls back to the org default when unset."
                    >
                      <MenuItem value="">
                        <em>(none — use org default)</em>
                      </MenuItem>
                      {surveyConfigs
                        .filter((s) => s.is_supplemental && s.id !== config.id)
                        .map((s) => (
                          <MenuItem key={s.id} value={s.id}>
                            {s.name}
                          </MenuItem>
                        ))}
                    </TextField>
                  </span>
                </Tooltip>
                {(partnerOrgs.length > 0 || config.partner_org_id) && (
                  <TextField
                    select
                    fullWidth
                    label="Share submitted reports with partner"
                    value={config.partner_org_id ?? ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      setConfig((prev) => ({
                        ...prev,
                        partner_org_id: v === "" ? null : v,
                      }));
                      setIsDirty(true);
                    }}
                    error={Boolean(partnerOrgsError)}
                    helperText={
                      partnerOrgsError
                        ? `${partnerOrgsError} You can still clear the current selection.`
                        : "New submissions to this report type are shared with the selected partner org's back office (RubiPARTNER). Existing reports are unaffected."
                    }
                  >
                    <MenuItem value="">
                      <em>(not shared)</em>
                    </MenuItem>
                    {partnerOrgs.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.title}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                <FormControlLabel
                  control={
                    <Switch
                      checked={
                        config.prefill_report_number_in_supplemental_url ??
                        false
                      }
                      onChange={handleChange(
                        "prefill_report_number_in_supplemental_url",
                      )}
                    />
                  }
                  label={
                    <LabelWithHelp
                      label="Prefill report number"
                      help="Append the original report number to the supplementary link so it pre-populates when opened."
                    />
                  }
                />
              </Stack>
            </Box>

            <Box>
              <Typography variant="overline" color="text.secondary">
                Carry over fields to supplementary
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1, mt: 0.5 }}
              >
                Answer keys copied from the original report into the dedicated
                supplementary link as pre-fill values. Enter SurveyJS question
                names (e.g. person_reportingParty-firstName, incident_where).
                Separate with commas, spaces, or new lines.
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Carry-over field keys"
                placeholder="person_reportingParty-firstName, incident_where"
                value={carryOverKeysInput}
                disabled={config.is_supplemental}
                onChange={(e) => handleCarryOverKeysChange(e.target.value)}
                helperText="Only keys whose question exists in the supplementary survey are carried over."
              />
            </Box>

            <Box>
              <Typography variant="overline" color="text.secondary">
                CC email recipients
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1, mt: 0.5 }}
              >
                Addresses CC'd on every email this report type sends to the
                reporting party (confirmation, approval, rejection, follow-up,
                etc.). Separate multiple addresses with commas, spaces, or new
                lines.
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="CC addresses"
                placeholder="records@example.org, archive@example.org"
                value={ccEmailsInput}
                onChange={(e) => handleCcEmailsChange(e.target.value)}
                error={ccEmailsErrors.length > 0}
                helperText={
                  ccEmailsErrors.length > 0
                    ? `Invalid email${ccEmailsErrors.length === 1 ? "" : "s"}: ${ccEmailsErrors.join(", ")}`
                    : " "
                }
              />
            </Box>

            <Box>
              <Typography variant="overline" color="text.secondary">
                Email body messages
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1, mt: 0.5 }}
              >
                Body text inserted into the email sent to the submitter on each
                status change. Leave blank to fall back to the org-level
                default.
              </Typography>
              <Stack spacing={2}>
                {RESPONSE_NOTE_STATUSES.map((status) => (
                  <ResponseNoteSection
                    key={status.key}
                    status={status}
                    value={config.response_notes ?? null}
                    onChange={(next) => {
                      setConfig((prev) => ({ ...prev, response_notes: next }));
                      setIsDirty(true);
                    }}
                  />
                ))}
              </Stack>
            </Box>
          </Stack>
        </TabSection>
      )}

      {activeTab === "review" && (
        <TabSection>
          <Stack spacing={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={config.auto_approve_on_submit ?? false}
                  onChange={handleChange("auto_approve_on_submit")}
                />
              }
              label="Auto approve on submit"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={config.auto_approve_linked_supplementals ?? false}
                  onChange={handleChange("auto_approve_linked_supplementals")}
                />
              }
              label="Auto approve linked supplementals"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={config.auto_reject_modification_requested}
                  onChange={handleChange("auto_reject_modification_requested")}
                />
              }
              label={
                <LabelWithHelp
                  label="Auto reject follow-up after 30 days"
                  help="Automatically reject submissions stuck in follow-up status for more than 30 days"
                />
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={config.mandatory_crime_codes ?? false}
                  onChange={handleChange("mandatory_crime_codes")}
                />
              }
              label="Mandatory crime codes"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={
                    config.clear_assigned_to_on_supplemental_linked ?? false
                  }
                  onChange={handleChange(
                    "clear_assigned_to_on_supplemental_linked",
                  )}
                />
              }
              label={
                <LabelWithHelp
                  label="Clear assigned reviewer when supplemental linked"
                  help="When a supplemental is linked to the original report, clear the existing reviewer assignment"
                />
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={config.clear_assigned_to_on_resubmitted ?? false}
                  onChange={handleChange("clear_assigned_to_on_resubmitted")}
                />
              }
              label={
                <LabelWithHelp
                  label="Clear assigned reviewer on resubmit"
                  help="When a citizen resubmits after a modification request, clear the existing reviewer assignment"
                />
              }
            />

            <Box>
              <Typography variant="subtitle1">Sample synopsis</Typography>
              <Typography variant="caption" color="text.secondary">
                Seed text given to the AI synopsis generator as a style guide
                when summarizing submissions for reviewers.
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={3}
                value={config.sample_synopsis ?? ""}
                onChange={handleChange("sample_synopsis")}
                sx={{ mt: 1 }}
              />
            </Box>

            <Box>
              <Typography variant="subtitle1">
                Default reviewer details
              </Typography>
              <Typography variant="caption" color="text.secondary">
                JSON object pre-populated into the reviewer details form when a
                new submission arrives. Leave blank to use no defaults.
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={4}
                value={defaultReviewerJson}
                onChange={(e) => handleDefaultReviewerChange(e.target.value)}
                placeholder='{"priority": "normal"}'
                error={defaultReviewerJsonError != null}
                helperText={defaultReviewerJsonError ?? " "}
                sx={{ mt: 1, fontFamily: "monospace" }}
              />
            </Box>

            <Box>
              <Typography variant="subtitle1">Reviewer details form</Typography>
              <Typography variant="caption" color="text.secondary">
                SurveyJS schema for the reviewer details form shown beside a
                submission. Leave blank for no reviewer details form.
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={8}
                value={reviewerSurveyJson}
                onChange={(e) => handleReviewerSurveyChange(e.target.value)}
                placeholder='{"elements": []}'
                error={reviewerSurveyJsonError != null}
                helperText={reviewerSurveyJsonError ?? " "}
                sx={{ mt: 1, fontFamily: "monospace" }}
              />
            </Box>
          </Stack>
        </TabSection>
      )}

      {activeTab === "export" && (
        <TabSection>
          <Stack spacing={4}>
            <Stack spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.export_to_rms ?? true}
                    onChange={handleChange("export_to_rms")}
                  />
                }
                label="Export to RMS"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={config.include_attachments_in_rms_export ?? true}
                    onChange={handleChange("include_attachments_in_rms_export")}
                  />
                }
                label="Include attachments in RMS export"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={config.include_attachments_in_dems_export ?? true}
                    onChange={handleChange(
                      "include_attachments_in_dems_export",
                    )}
                  />
                }
                label="Include attachments in DEMs export"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={config.include_pdf_in_emails ?? true}
                    onChange={handleChange("include_pdf_in_emails")}
                  />
                }
                label="Include PDF in emails"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={config.include_images_in_pdf ?? false}
                    onChange={handleChange("include_images_in_pdf")}
                  />
                }
                label="Include image attachments in PDF"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={config.export_as_message_template ?? true}
                    onChange={handleChange("export_as_message_template")}
                  />
                }
                label="Export as message template"
              />
            </Stack>

            <Box>
              <Typography variant="overline" color="text.secondary">
                RMS incident codes
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1, mt: 0.5 }}
              >
                Codes stamped onto this report type&apos;s RMS export. Leave a
                field blank to fall back to the value noted under it.
              </Typography>
              <Grid container spacing={2}>
                {RMS_INCIDENT_CODE_FIELDS.map((field) => (
                  <Grid key={field.key} size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField
                      fullWidth
                      label={field.label}
                      value={config.rms_incident_codes?.[field.key] ?? ""}
                      onChange={(e) =>
                        handleRmsCodeChange(field.key, e.target.value)
                      }
                      helperText={
                        field.fallback
                          ? `Falls back to: ${field.fallback}`
                          : "No value sent when blank"
                      }
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box>
              <Typography variant="overline" color="text.secondary">
                Fields excluded from export
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1, mt: 0.5 }}
              >
                Question groups dropped from this report type&apos;s RMS export.
                The export acts on {EXCLUDABLE_EXPORT_FIELDS.join(", ")}; any
                other name is only used by the form linter, which flags excluded
                fields that still exist on the form.
              </Typography>
              <Autocomplete
                multiple
                freeSolo
                options={EXCLUDABLE_EXPORT_FIELDS}
                value={config.fields_to_exclude ?? []}
                onChange={(_, next) => handleExcludedFieldsChange(next)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Excluded fields"
                    placeholder="Add field..."
                  />
                )}
              />
            </Box>
          </Stack>
        </TabSection>
      )}

      {activeTab === "translations" && (
        <TabSection>
          <Stack spacing={3}>
            <Typography variant="body2" color="text.secondary">
              Auto-translated copies of the name and description, regenerated
              when either changes. Read-only here; edit via the source fields on
              the General tab.
            </Typography>

            <ReadOnlyJsonField
              label="Translated name & description"
              value={config.translated_name_description}
            />
          </Stack>
        </TabSection>
      )}

      <div className="actions">
        <Button variant="outlined" onClick={handleBack}>
          Back
        </Button>
        {!isNewConfig && originalConfig?.id && (
          <Button
            variant="outlined"
            color="error"
            onClick={handleDeactivateClick}
            disabled={isSaving || isDeactivating}
          >
            Deactivate
          </Button>
        )}
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={
            isSaving ||
            !isDirty ||
            isDuplicateSurveyClass(config.survey_class) ||
            !isValidUrl(config.external_url || "") ||
            defaultReviewerJsonError != null ||
            reviewerSurveyJsonError != null ||
            ccEmailsErrors.length > 0
          }
        >
          {isSaving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      <Dialog open={deactivateModalOpen} onClose={handleDeactivateCancel}>
        <DialogTitle>Deactivate survey configuration?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will remove &quot;{originalConfig?.name}&quot; from the active
            survey configurations. The configuration can be re-added later by
            creating a new one with the same survey class.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeactivateCancel} disabled={isDeactivating}>
            Cancel
          </Button>
          <Button
            onClick={handleDeactivateConfirm}
            color="error"
            variant="contained"
            disabled={isDeactivating}
          >
            {isDeactivating ? "Deactivating..." : "Deactivate"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

const TabSection = ({ children }: { children: ReactNode }) => (
  <Box sx={{ py: 1 }}>{children}</Box>
);

const LabelWithHelp = ({ label, help }: { label: string; help: string }) => (
  <>
    {label}
    <Typography variant="caption" display="block" sx={{ ml: 0 }}>
      {help}
    </Typography>
  </>
);

type ResponseNoteStatusKey =
  | "on_approved"
  | "on_rejected"
  | "on_escalated"
  | "on_confirmation";

type ResponseNoteStatus = {
  key: ResponseNoteStatusKey;
  frKey: keyof ResponseNotes; // matching `_fr` field
  label: string;
};

const RESPONSE_NOTE_STATUSES: ResponseNoteStatus[] = [
  { key: "on_approved", frKey: "on_approved_fr", label: "Approved" },
  { key: "on_rejected", frKey: "on_rejected_fr", label: "Rejected" },
  { key: "on_escalated", frKey: "on_escalated_fr", label: "Escalated" },
  {
    key: "on_confirmation",
    frKey: "on_confirmation_fr",
    label: "Confirmation email",
  },
];

const ResponseNoteSection = ({
  status,
  value,
  onChange,
}: {
  status: ResponseNoteStatus;
  value: ResponseNotes | null;
  onChange: (next: ResponseNotes | null) => void;
}) => {
  const en = (value?.[status.key] ?? null) as ResponseNote | null;
  const fr = (value?.[status.frKey] ?? null) as ResponseNote | null;

  const update = (field: keyof ResponseNotes, patch: Partial<ResponseNote>) => {
    const current = (value?.[field] ?? null) as ResponseNote | null;
    const merged: ResponseNote = {
      body_content: current?.body_content ?? null,
      footer_content: current?.footer_content ?? null,
      ...patch,
    };
    const isEmpty =
      !merged.body_content?.trim() && !merged.footer_content?.trim();
    const next: ResponseNotes = {
      ...(value ?? {}),
      [field]: isEmpty ? null : merged,
    };
    // If every field became empty, null out the whole object to keep payload clean.
    const allEmpty = Object.values(next).every((v) => v == null);
    onChange(allEmpty ? null : next);
  };

  const hasContent =
    en?.body_content ||
    en?.footer_content ||
    fr?.body_content ||
    fr?.footer_content;

  return (
    <Accordion defaultExpanded={!!hasContent}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography>{status.label}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" gutterBottom>
              English
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Body"
              value={en?.body_content ?? ""}
              onChange={(e) =>
                update(status.key, { body_content: e.target.value || null })
              }
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" gutterBottom>
              Français
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Body"
              value={fr?.body_content ?? ""}
              onChange={(e) =>
                update(status.frKey, { body_content: e.target.value || null })
              }
            />
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

const ReadOnlyJsonField = ({
  label,
  value,
}: {
  label: string;
  value: unknown;
}) => {
  const display = value == null ? "(not set)" : JSON.stringify(value, null, 2);
  return (
    <Box>
      <Typography variant="subtitle1">{label}</Typography>
      <TextField
        fullWidth
        multiline
        minRows={3}
        value={display}
        disabled
        sx={{ mt: 1, fontFamily: "monospace" }}
      />
    </Box>
  );
};
